// app/src/screens/SignUp.jsx — THE signup page (D-AUTH parity §1, mirroring Quocia-FB's
// SignUpForm): name/email/password with the reference's live password checklist (8–128 +
// lower + upper + digit), marketing opt-in, and the two OAuth buttons. Email/password
// signup calls POST /v1/auth/signup (the server validates the email, mints the canonical
// uid, sends the localized verification email), then signs straight in — the reference's
// exact client choreography. Refusals render the server's reason + typo suggestion
// verbatim.

import React, { useMemo, useState } from 'react';
import { Link, navigate } from '@common/router.js';
import { useI18n } from '../i18n/index.jsx';
import { useSession } from '../lib/session.jsx';
import { useSeedLangCookie, publicLocale } from '../lib/locale-sync.jsx';
import { startOAuth } from '../lib/oauth.js';
import { AuthFrame, OrDivider, INPUT_STYLE } from './SignIn.jsx';

function pwChecks(password) {
  return {
    length: password.length >= 8 && password.length <= 128,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
  };
}

function Check({ ok, label }) {
  return <span style={{ color: ok ? 'var(--mint)' : 'var(--text-faint)', fontSize: 12 }}>{label} {ok ? '✓' : '·'}</span>;
}

export default function SignUp() {
  const { t } = useI18n();
  const { auth, api } = useSession();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [optIn, setOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);       // { reason, suggestion? }
  useSeedLangCookie();

  const checks = useMemo(() => pwChecks(password), [password]);
  const pwOk = checks.length && checks.lowercase && checks.uppercase && checks.number;
  const canSubmit = !busy && name.trim() && email.trim() && pwOk;

  async function submit() {
    setErr(null); setBusy(true);
    try {
      const r = await api.post('auth/signup', {
        name: name.trim(),
        email: email.trim(),
        password,
        marketingOptIn: optIn,
        uauLocale: publicLocale(),           // seeds the ACCOUNT language (D-AUTH §3)
        clientOrigin: window.location.origin, // localhost verification links (dev project testing)
      });
      const b = r.body || {};
      if (r.kind === 'ok' && b.success) {
        await auth.signInEmail(email.trim(), password); // the reference: sign in right after
        navigate('/'); // land on a clean URL — the Verify-Email gate takes it from here
        return;
      }
      setErr({
        reason: b.reason || b.message || r.error || t('common.error'),
        suggestion: b.suggestion || null,
      });
    } catch (e) {
      setErr({ reason: e.message, suggestion: null });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame title={t('signup.title')} subtitle={t('signup.subtitle')}>
      {auth.mode === 'dev' ? (
        <button className="q-btn press btn-primary" style={{ width: '100%' }} onClick={() => auth.signInDev()}>{t('signin.dev')}</button>
      ) : (
        <>
          <button className="q-btn press btn-primary" style={{ width: '100%' }} onClick={() => startOAuth('google')}>{t('signup.google')}</button>
          <button className="q-btn press btn-ghost" style={{ width: '100%', marginBlockStart: 8 }} onClick={() => startOAuth('linkedin')}>{t('signup.linkedin')}</button>
          <OrDivider />
          <input type="text" placeholder={t('signup.namePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} style={INPUT_STYLE} autoComplete="name" />
          <input type="email" placeholder={t('signin.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} style={INPUT_STYLE} autoComplete="email" />
          <input type="password" placeholder={t('signin.passwordPlaceholder')} value={password} onChange={(e) => setPassword(e.target.value)} style={INPUT_STYLE} autoComplete="new-password" />
          {password && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', margin: '2px 2px 8px' }}>
              <Check ok={checks.length} label={t('signup.pwLength')} />
              <Check ok={checks.lowercase} label={t('signup.pwLower')} />
              <Check ok={checks.uppercase} label={t('signup.pwUpper')} />
              <Check ok={checks.number} label={t('signup.pwNumber')} />
            </div>
          )}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '4px 2px 12px', cursor: 'pointer' }}>
            <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} style={{ marginBlockStart: 3 }} />
            <span className="muted" style={{ fontSize: 12.5, lineHeight: 1.45 }}>{t('signup.marketingOptIn')}</span>
          </label>
          <button className="q-btn press btn-primary" style={{ width: '100%' }} disabled={!canSubmit} onClick={submit}>
            {busy ? t('signup.creating') : t('signup.cta')}
          </button>
          <p className="muted" style={{ fontSize: 13, textAlign: 'center', margin: '14px 0 0' }}>
            {t('signup.haveAccount')} <Link to="/signin" style={{ color: 'var(--ink)', fontWeight: 600 }}>{t('signup.signinLink')}</Link>
          </p>
        </>
      )}
      {err && (
        <div style={{ margin: '12px 0 0' }}>
          <p style={{ color: 'var(--coral)', fontSize: 13, margin: 0 }}>{err.reason}</p>
          {err.suggestion && (
            <button className="q-btn" style={{ padding: 0, border: 'none', background: 'none', color: 'var(--ink)', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBlockStart: 4 }}
              onClick={() => { setEmail(err.suggestion); setErr(null); }}>
              {t('signup.useSuggestion', { email: err.suggestion })}
            </button>
          )}
        </div>
      )}
    </AuthFrame>
  );
}
